<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '../logger.ts'

import { ref, onMounted } from 'vue'
import { GetProductRootUrl, GetRootUrl } from '../stores/SettingsStore'
import { GetFullProductImageDictionary } from '../services/SkuService.ts'
import { CheckInvoiceExists, VerifyInvoiceIdentity } from '../services/CartService.ts'
import type { IAoeCart, IAoeInvoiceResponse } from '../types/CartTypes.ts'
import { handleError } from '../services/ErrorService'

import AoeInvoicePayment from '../components/AoeInvoicePayment.vue'

import { SetIsLoading } from '../services/AoeHelpers'
import { Button as KButton } from '@progress/kendo-vue-buttons'

const logger = useLogger()
setStoreLogger(logger)

const urlParams = new URLSearchParams(window.location.search)
const invoicenumber = urlParams.get('invoicenumber')
const productRootUrl = ref('')
const rootUrl = ref('')
const productCodes = ref<string[]>([])
const productImageUrls = ref<Record<string, string>>({})

const invoiceCart = ref<IAoeInvoiceResponse | null>(null)
const cart = ref<IAoeCart | null>(null)
const invoiceExists = ref(false)
const identityVerified = ref(false)
const email = ref('')

onMounted(async () => {
  logger.debug('onMounted')
  productRootUrl.value = await GetProductRootUrl()
  rootUrl.value = await GetRootUrl()

  logger.debug('AoeInvoice','onMounted', { productRootUrl: productRootUrl.value, rootUrl: rootUrl.value })
  SetIsLoading(true)
  await checkInvoiceExists()
})

async function checkInvoiceExists() {
  if (!invoicenumber) return

  try {
    await CheckInvoiceExists({ invoiceNumber: invoicenumber })
    invoiceExists.value = true
    logger.debug('checkInvoiceExists complete', { invoicenumber, invoiceExists: invoiceExists.value, identityVerified: identityVerified.value })
  } catch (error) {
    logger.error('InvoiceExists error', error)
    await handleError(error, t)
  } finally {
    SetIsLoading(false)
  }
}

async function verifyIdentity() {
  if (!invoicenumber || !email.value) return

  try {
    SetIsLoading(true)
    const data = await VerifyInvoiceIdentity({
      invoiceNumber: invoicenumber,
      emailAddress: email.value
    })

    if (data != null) {
      invoiceCart.value = data
      cart.value = data.item
      for (const items of cart.value.items) {
        productCodes.value.push(items.productCode)
      }

      productImageUrls.value = await GetFullProductImageDictionary(productCodes.value)

      if (cart.value.invoice != null && cart.value.invoice.invoiceStatus.codeId.toLowerCase() == "canceled") {
        throw new Error(t('errors.invoiceIsCanceled'))
      }

      identityVerified.value = true
    }

    SetIsLoading(false)
  } catch (error) {
    logger.error('VerifyInvoiceIdentity error', error)
    SetIsLoading(false)
    await handleError(error, t)
  }
}

function updateCartHandler() {
  logger.debug('updateCartHandler')
  refreshInvoiceData()
}

async function refreshInvoiceData() {
  if (!invoicenumber) return

  try {
    SetIsLoading(true)
    const data = await VerifyInvoiceIdentity({
      invoiceNumber: invoicenumber,
      emailAddress: email.value
    })

    if (data != null) {
      invoiceCart.value = data
      cart.value = data.item
      productCodes.value = []
      for (const items of cart.value.items) {
        productCodes.value.push(items.productCode)
      }

      productImageUrls.value = await GetFullProductImageDictionary(productCodes.value)
    }

    SetIsLoading(false)
  } catch (error) {
    logger.error('RefreshInvoiceData error', error)
    SetIsLoading(false)
    await handleError(error, t)
  }
}

</script>

<template>
  <div>
    <!-- Invoice exists but identity not yet verified — show email form -->
    <div v-if="invoiceExists && !identityVerified" class="aoe-invoice-verify-identity">
      <div class="row section-header-row">
        <div class="col-12 section-header-div invoice-section-header">
          {{ t('invoice.InvoiceHeader') }}
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12 verify-identity-instruction-div">
          <p>{{ t('invoice.VerifyIdentityInstruction') }}</p>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12 col-md-6 verify-identity-form-div">
          <label class="verify-identity-label" for="invoice-email">{{ t('invoice.EmailLabel') }}</label>
          <input
            id="invoice-email"
            v-model="email"
            type="email"
            class="k-textbox verify-identity-input"
            :placeholder="t('invoice.EmailPlaceholder')"
            @keyup.enter="verifyIdentity"
          />
          <KButton
            :rounded="'large'"
            class="baseBtn primaryBtn verify-identity-button mt-3"
            :disabled="!email"
            @click="verifyIdentity"
          >
            {{ t('invoice.VerifyIdentityButton') }}
          </KButton>
        </div>
      </div>
    </div>

    <!-- Identity verified — show the payment UI -->
    <AoeInvoicePayment
      v-if="identityVerified && invoicenumber"
      :invoiceCart="invoiceCart"
      :cart="cart"
      :productImageUrls="productImageUrls"
      :invoicenumber="invoicenumber"
      :rootUrl="rootUrl"
      :emailAddress="email"
      @update-cart="updateCartHandler"
    />
  </div>
</template>

<style lang="scss">
.aoe-invoice-verify-identity {
  max-width: 600px;
  margin: 0 auto;
}

.section-header-div {
  @include typography-body-large;
  @include font-weight-semibold;
  padding: $spacing-3 $spacing-4;
  background-color: transparent;
  border-bottom: 2px solid var(--primary-text-color, var(--vt-c-teal-dark));
  margin-bottom: 0;
}

.invoice-section-header {
  color: var(--primary-text-color, var(--vt-c-teal-dark));
  letter-spacing: 0.5px;
}

.verify-identity-instruction-div {
  padding: $spacing-4;
  @include typography-body;
  color: var(--color-text);
}

.verify-identity-form-div {
  padding: $spacing-4;
}

.verify-identity-label {
  @include typography-small;
  @include font-weight-semibold;
  display: block;
  margin-bottom: $form-label-margin;
  color: var(--color-text);
}

.verify-identity-input {
  width: 100%;
  padding: $button-padding-sm-y $button-padding-sm-x;
  border: 1px solid var(--color-border);
  border-radius: $input-radius;
}

.verify-identity-button {
  @include font-weight-medium;
  min-width: 150px;
}
</style>
